import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import CustomerPicker from "@/v2-backoffice-client/customer/CustomerPicker"
import LicensePlanSelect from "@/v2-backoffice-client/license/LicensePlanSelect"
import LicenseProductSelect from "@/v2-backoffice-client/license/LicenseProductSelect"
import LicenseCreateSeatLimit from "@/v2-backoffice-client/license/create/LicenseCreateSeatLimit"
import { useLicenseCreateMutation } from "@/v2-backoffice-client/license/create/license.create.mutation"
import {
  EMPTY_SEAT_LIMIT_DRAFT,
  isSeatLimitCountValid,
  seatLimitCountOf,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import {
  dateInputToDatetime,
  evictLicenseQueries,
  getDateInputToday,
} from "@/v2-backoffice-client/license/license.utils"
import {
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_TRIAL,
  LICENSE_PLAN_NONE_VALUE,
} from "@/v2-backoffice-client/license/license.constants"

// D3 default: a fresh trial runs 14 days unless sales asks otherwise.
const DEFAULT_TRIAL_DAYS = 14

function getDefaultTrialEnd(): string {
  const end = new Date()
  end.setDate(end.getDate() + DEFAULT_TRIAL_DAYS)
  const month = String(end.getMonth() + 1).padStart(2, "0")
  const day = String(end.getDate()).padStart(2, "0")
  return `${end.getFullYear()}-${month}-${day}`
}

/**
 * The slot a bridge trial is granted over: a license billing (Fenerum)
 * holds for the customer but has not made live — Scheduled, Expired or Not
 * starting — so nobody is let in and no row action applies.
 */
export type LicenseCreateBridge = {
  productCode: number
  productName: string
  seatsAreAssigned: boolean
  planId: number | null
  /** The slot's status display name, for the explanation. */
  statusName: string
}

export type LicenseCreateDialogProps = DialogProps & {
  /** Scopes the grant to one customer (panel flow); omitted on the board. */
  customerId?: number
  customerName?: string
  /**
   * Bridges a slot billing holds but has not made live: the product is the
   * slot's, the plan starts as the billed one, and the grant is a trial —
   * the one operator row the hourly sync leaves alone (an Active grant would
   * be taken back within the hour, and the proc refuses it). Needs
   * customerId.
   */
  bridge?: LicenseCreateBridge
}

// Grants a license by hand: a trial by default (full trial, no card — D3), or
// an Active manual grant for arrangements billing can't express, like a child
// company paid through its parent's account. Fenerum-derived licenses never
// come from here. Every grant covers everyone at the customer (FUS-2766); for
// a seated product (the MCP servers) the operator may fold the second step
// in and tick the people now, in which case the license lands already
// limited to them, in one transaction. A seat count alone never limits
// anything, so the old trap (a count, nobody named, nobody in) cannot recur.
function LicenseCreateDialog(props: LicenseCreateDialogProps) {
  const {
    customerId,
    customerName,
    bridge,
    open,
    onOpenChange,
    ...dialogProps
  } = props

  const initialProductCode = bridge ? String(bridge.productCode) : ""
  const initialIsSeatedProduct = bridge?.seatsAreAssigned ?? false
  const initialPlanId =
    bridge?.planId != null ? String(bridge.planId) : LICENSE_PLAN_NONE_VALUE

  const [pickedCustomer, setPickedCustomer] = useState<{
    Id: number
    Name?: string | null
  } | null>(null)
  const [productCode, setProductCode] = useState<string>(initialProductCode)
  const [isSeatedProduct, setIsSeatedProduct] = useState(initialIsSeatedProduct)
  const [planId, setPlanId] = useState<string>(initialPlanId)
  const [isTrialPicked, setIsTrialPicked] = useState(true)
  // A bridge is a trial whatever the picker says; the picker is not shown.
  const isTrial = bridge ? true : isTrialPicked
  const [trialEndsAt, setTrialEndsAt] = useState(getDefaultTrialEnd())
  const [seats, setSeats] = useState("")
  // The seated product's optional "only named people" step.
  const [isSeatLimited, setIsSeatLimited] = useState(false)
  const [seatLimit, setSeatLimit] = useState(EMPTY_SEAT_LIMIT_DRAFT)

  const targetCustomerId = customerId ?? pickedCustomer?.Id
  const targetCustomerName = customerName ?? pickedCustomer?.Name
  const hasSeatLimit = isSeatedProduct && isSeatLimited
  // For a product whose seats are not named (Placepoint) the count is the
  // cap reported against the customer's users. For a seated product it is
  // what the customer pays for, asked only together with the people.
  const seatCount = isSeatedProduct
    ? hasSeatLimit
      ? seatLimitCountOf(seatLimit, true)
      : null
    : seats
      ? Number(seats)
      : null

  const [createLicense, { loading, error }] = useLicenseCreateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      const created = data.createLicense
      toast({
        variant: "success",
        title: bridge
          ? "Bridged with a trial"
          : isTrial
            ? "Trial granted"
            : "License created",
        description: `${created.CustomerName ?? "The customer"} now holds ${isTrial ? "a trial" : "an active license"} for ${created.ProductName}. ${
          created.SeatsRestricted
            ? `Only the ${created.SeatsAssigned} named ${created.SeatsAssigned === 1 ? "person has" : "people have"} it.`
            : `Everyone at the customer has it${created.SeatsAreAssigned ? "; limit it to named people from the row's Seats if the deal says so" : ""}.`
        }${bridge ? " Billing takes the product back when the trial ends, or when you convert it." : ""}`,
      })
      onOpenChange?.(false)
      setPickedCustomer(null)
      setProductCode(initialProductCode)
      setIsSeatedProduct(initialIsSeatedProduct)
      setPlanId(initialPlanId)
      setIsTrialPicked(true)
      setTrialEndsAt(getDefaultTrialEnd())
      setSeats("")
      setIsSeatLimited(false)
      setSeatLimit(EMPTY_SEAT_LIMIT_DRAFT)
    },
  })

  // A seated product's license grants nothing without a license from the
  // catalog (the proc refuses it too, FUS-2766); the picker fills it in. A
  // limit with nobody ticked is not a grant to nobody: the step must name
  // someone or be switched off.
  const canSubmit =
    !!targetCustomerId &&
    !!productCode &&
    (!isTrial || !!trialEndsAt) &&
    (!isSeatedProduct || planId !== LICENSE_PLAN_NONE_VALUE) &&
    (!hasSeatLimit ||
      (seatLimit.userIds.length > 0 && isSeatLimitCountValid(seatLimit)))

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top" className="max-h-[85vh] overflow-y-auto">
        <Dialog.Header>
          <Dialog.Title>
            {bridge
              ? `Bridge With a Trial — ${targetCustomerName ?? bridge.productName}`
              : targetCustomerName
                ? `Grant License — ${targetCustomerName}`
                : "Grant License"}
          </Dialog.Title>
          <Dialog.Description>
            {bridge
              ? `Billing (Fenerum) holds ${bridge.productName} for this customer as ${bridge.statusName}, so nobody is let in. A trial bridges that gap: it is the one license the hourly billing sync leaves alone, and billing takes the product back when the trial ends, or when you convert it. Fix the subscription in Fenerum alongside.`
              : "Grants a trial (default) or an active license by hand. Licenses derived from billing arrive via the Fenerum sync, not here."}
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          {!customerId && (
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerPicker
                onSelect={(customer) => {
                  setPickedCustomer({ Id: customer.Id, Name: customer.Name })
                }}
              >
                {pickedCustomer?.Name ?? "Pick a customer..."}
              </CustomerPicker>
            </div>
          )}

          <div className="space-y-2">
            <Label>Product</Label>
            {bridge ? (
              // The slot's product: a bridge is granted over one license,
              // so there is nothing to pick.
              <p className="text-sm">{bridge.productName}</p>
            ) : (
              <LicenseProductSelect
                value={productCode}
                onValueChange={(next, product) => {
                  setProductCode(next)
                  setIsSeatedProduct(product?.SeatsAreAssigned ?? false)
                  // A license belongs to a product; changing the product drops a
                  // pick that may no longer fit, and the named-people step with it.
                  setPlanId(LICENSE_PLAN_NONE_VALUE)
                  setIsSeatLimited(false)
                  setSeatLimit(EMPTY_SEAT_LIMIT_DRAFT)
                }}
              />
            )}
          </div>

          {productCode && (
            <div className="space-y-2">
              <Label>License</Label>
              <LicensePlanSelect
                value={planId}
                productCode={productCode}
                requirePlan={isSeatedProduct}
                onValueChange={setPlanId}
              />
              <p className="text-xs text-muted-foreground">
                {isSeatedProduct
                  ? "Which license the customer is on. Its packages are what let people in, so one is required; the product's own license is picked for you."
                  : 'Which license the customer is on — "a trial of Professional". Its packages are what the customer gets once features are locked within a package. None is for an add-ons-only grant and unlocks nothing by itself.'}
              </p>
            </div>
          )}

          {/* A bridge is a trial by definition (an Active grant over a slot
              billing holds is taken back by the next sync), so the picker
              only shows for a plain grant. */}
          {!bridge && (
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={isTrial ? "trial" : "active"}
                onValueChange={(next) => {
                  setIsTrialPicked(next === "trial")
                }}
              >
                <Select.Trigger size="md" className="w-full">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="trial">
                    Trial — time-boxed, converts explicitly
                  </Select.Item>
                  <Select.Item value="active">
                    Active — manual grant without a billing record
                  </Select.Item>
                </Select.Content>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {isTrial && (
              <div className="space-y-2">
                <Label htmlFor="licenseCreateTrialEndsAt">Trial ends</Label>
                <Input
                  id="licenseCreateTrialEndsAt"
                  type="date"
                  required
                  min={getDateInputToday()}
                  value={trialEndsAt}
                  onChange={(e) => {
                    setTrialEndsAt(e.target.value)
                  }}
                />
              </div>
            )}

            {/* The cap for products whose seats are not named. A seated
                product's count belongs with its limit (Seats on the row,
                after the grant), where the people are named with it. */}
            {!isSeatedProduct && (
              <div className="space-y-2">
                <Label htmlFor="licenseCreateSeats">Seats (optional)</Label>
                <Input
                  id="licenseCreateSeats"
                  type="number"
                  min={1}
                  value={seats}
                  onChange={(e) => {
                    setSeats(e.target.value)
                  }}
                />
              </div>
            )}
          </div>

          {isSeatedProduct && targetCustomerId && (
            <LicenseCreateSeatLimit
              customerId={targetCustomerId}
              customerName={targetCustomerName ?? "the customer"}
              isLimited={isSeatLimited}
              onIsLimitedChange={setIsSeatLimited}
              draft={seatLimit}
              onDraftChange={setSeatLimit}
            />
          )}
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={loading}
            disabled={!canSubmit}
            data-feature-code="UI_BackofficeLicense_Create_Save"
            onClick={() => {
              if (!targetCustomerId) return
              createLicense({
                variables: {
                  input: {
                    CustomerId: targetCustomerId,
                    ProductCode: Number(productCode),
                    LicenseStatusCode: isTrial
                      ? LICENSE_STATUS_TRIAL
                      : LICENSE_STATUS_ACTIVE,
                    TrialEndsAt: isTrial
                      ? dateInputToDatetime(trialEndsAt)
                      : undefined,
                    Seats: seatCount ?? undefined,
                    SeatUserIds: hasSeatLimit ? seatLimit.userIds : undefined,
                    PlanId:
                      planId === LICENSE_PLAN_NONE_VALUE
                        ? undefined
                        : Number(planId),
                  },
                },
              })
            }}
          >
            {loading
              ? "Granting..."
              : bridge
                ? "Bridge With a Trial"
                : isTrial
                  ? "Grant Trial"
                  : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicenseCreateDialog
